/**
 * Shared A/B-variants editor for posts.
 *
 * One UI, two call sites:
 *   - wizard step 4 ("Креатив") — `<CreativeVariantsEditor>` wired to the draft
 *     store; bloggerIds prop turns on the per-channel assignment block.
 *   - campaign Kanban → "Креативы в кампании" drawer — same component, no
 *     assignment block, variants live in local React state until "Сохранить".
 *
 * Changing the form (fields, validation, layout) here changes both call sites.
 *
 * Hard rule: the component is fully controlled (variants in, onChange out).
 * That way the wrapping page decides where the variants are persisted —
 * draft store for the wizard, backend POST for the drawer.
 */
import { useEffect, useRef, useState } from "react";
import { HelpCircle, LayoutGrid, Loader2, Pencil, Plus, Sparkles, X } from "lucide-react";
import { useCreativeRewrite } from "@/shared/api/wb";
import { useDraftStore } from "@/shared/store/draft";
import { Card, Field, PlatformIcon, type Platform } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import type { CreativeVariant } from "@/shared/store/draft";
import { CreativeEditor } from "./CreativeEditor";
import { TelegramPost } from "./TelegramPost";

export const MAX_VARIANTS = 10;

// Platforms that have a post-based preview (other platforms use different formats)
const PREVIEW_PLATFORMS: { key: string; label: string }[] = [
  { key: "tg",  label: "Telegram" },
  { key: "vk",  label: "VK" },
  { key: "max", label: "MAX" },
];

function genErid(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  return "Kra" + Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export const SEED_VARIANT: CreativeVariant = {
  id: "cv1", name: "Вариант 1", tone: "Дружеский",
  text: "",
  link: "",
  erid: genErid(),
  media: [],
};

export function makeSeedVariant(index: number, opts?: { link?: string }): CreativeVariant {
  return {
    ...SEED_VARIANT,
    id: `cv${Date.now() % 100000}-${index}`,
    name: `Вариант ${index}`,
    link: opts?.link ?? "",
    erid: genErid(),
  };
}

export type CreativeVariantsEditorProps = {
  variants: CreativeVariant[];
  onChange: (vs: CreativeVariant[]) => void;
  /** Show the "5 tips" hint card above the variant tabs. Default true. */
  showHints?: boolean;
  /** Auto-call AI rewrite on mount when the first variant is empty and product is filled. */
  autoGenerateOnEmpty?: boolean;
};

export function CreativeVariantsEditor({
  variants, onChange, showHints = true, autoGenerateOnEmpty = false,
}: CreativeVariantsEditorProps) {
  const [activeId, setActiveId] = useState<string>(() => variants[0]?.id ?? SEED_VARIANT.id);
  const [allCreativesOpen, setAllCreativesOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [hintsOpen, setHintsOpen] = useState(false);
  const hintsRef = useRef<HTMLDivElement>(null);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const [previewPlatform, setPreviewPlatform] = useState("tg");
  const rewrite = useCreativeRewrite();
  const draft = useDraftStore((s) => s.draft);

  // Unique platforms selected in the campaign (post-based only)
  const campaignPlatforms = [
    ...new Set(Object.values(draft.bloggerFormats).flat().map((s) => s.platform)),
  ].filter((p) => PREVIEW_PLATFORMS.some((pp) => pp.key === p));

  // If the active id falls out of the variants list (e.g. removeVariant), follow.
  useEffect(() => {
    if (!variants.some((v) => v.id === activeId) && variants[0]) setActiveId(variants[0].id);
  }, [variants, activeId]);

  // Hooks must run unconditionally, so this lives above the `!active` early
  // return below even though `onRewrite` (which it calls) is declared later —
  // function declarations are hoisted within the component body.
  const [autoTriggered, setAutoTriggered] = useState(false);
  useEffect(() => {
    if (autoTriggered || !autoGenerateOnEmpty) return;
    if (rewrite.isPending) return;
    if (!draft.productName?.trim()) return;
    if (variants.length !== 1) return;
    if (variants[0].text.trim() !== "") return;
    setAutoTriggered(true);
    void onRewrite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerateOnEmpty, draft.productName, variants.length]);

  const active = variants.find((v) => v.id === activeId) ?? variants[0];
  if (!active) return null;
  const hasMedia = (active.media?.length ?? 0) > 0;

  const renameVariant = (id: string, name: string) =>
    onChange(variants.map((v) => (v.id === id ? { ...v, name } : v)));
  const patchVariant = (id: string, p: Partial<CreativeVariant>) =>
    onChange(variants.map((v) => (v.id === id ? { ...v, ...p } : v)));
  const addVariant = () => {
    if (variants.length >= MAX_VARIANTS) return;
    const next = makeSeedVariant(variants.length + 1, { link: draft.productUrl });
    onChange([...variants, next]);
    setActiveId(next.id);
  };
  const removeVariant = (id: string) => {
    if (variants.length <= 1) return;
    const next = variants.filter((v) => v.id !== id).map((v, i) => ({ ...v, name: `Вариант ${i + 1}` }));
    onChange(next);
    if (activeId === id) setActiveId(next[0].id);
  };

  async function onRewrite() {
    if (rewrite.isPending) return;
    setRewriteError(null);
    if (!draft.productName?.trim()) {
      setRewriteError("Сначала заполните название продукта на шаге 01.");
      return;
    }
    try {
      const r = await rewrite.mutateAsync({
        tone: active.tone || "Дружеский",
        product_name: draft.productName,
        product_desc: draft.productDesc,
        product_url: draft.productUrl,
        product_category: draft.productCategory,
        current_text: active.text,
      });
      patchVariant(active.id, { text: r.text, link: draft.productUrl || active.link });
    } catch {
      setRewriteError("Не удалось сгенерировать. Попробуйте ещё раз.");
    }
  }

  return (
    <>
      <VariantTabs
        variants={variants} activeId={activeId} setActiveId={setActiveId}
        addVariant={addVariant} removeVariant={removeVariant}
        openAllCreatives={() => setAllCreativesOpen(true)}
      />

      <div className="grid grid-cols-[1fr_360px] gap-4 items-start">
        <Card className="!p-5 grid gap-3.5">
          {/* Name row with pencil hint */}
          <div className="flex items-center justify-between gap-2">
            <div className="relative group flex items-center gap-1.5 flex-1 max-w-[220px]">
              <input
                value={active.name}
                onChange={(e) => renameVariant(active.id, e.target.value)}
                className="font-display font-bold text-[16px] bg-transparent border-0 outline-none focus:bg-ink-6 px-1 rounded-sm w-full"
              />
              <Pencil
                size={12}
                className="text-ink-30 group-hover:text-ink-60 transition-colors flex-shrink-0 pointer-events-none"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {showHints && (
                <div className="relative" ref={hintsRef}>
                  <button
                    type="button"
                    onClick={() => setHintsOpen((o) => !o)}
                    className={cn(
                      "inline-flex items-center gap-1 h-8 px-2.5 rounded-pill border border-ink-12 text-[12px] font-display font-semibold transition-colors",
                      hintsOpen ? "bg-ink-6 text-ink border-ink/30" : "text-ink-60 hover:text-ink hover:border-ink/40",
                    )}
                  >
                    <Sparkles size={12} /> Советы
                  </button>
                  {hintsOpen && (
                    <HintsPopover onClose={() => setHintsOpen(false)} anchorRef={hintsRef} />
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={onRewrite}
                disabled={rewrite.isPending}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink hover:border-ink disabled:opacity-60 disabled:cursor-wait">
                {rewrite.isPending
                  ? <><Loader2 size={13} className="animate-spin" /> Пишем…</>
                  : <>✦ Переписать с AI</>}
              </button>
            </div>
          </div>

          {rewriteError && (
            <div className="px-3 py-2 -mt-1 rounded-md bg-danger/10 text-danger text-[12.5px]">
              {rewriteError}
            </div>
          )}

          <CreativeEditor
            text={active.text}
            onText={(t) => patchVariant(active.id, { text: t })}
            media={active.media ?? []}
            onMedia={(m) => patchVariant(active.id, { media: m })}
          />

          <Field label="Целевая ссылка" value={active.link} onChange={(e) => patchVariant(active.id, { link: e.target.value })} className="!font-mono" />
          <div className="text-[11.5px] text-ink-40 -mt-2">
            UTM-метки для отслеживания создаются автоматически для каждого канала при запуске — посмотреть и поправить можно будет в карточке размещения на канбане кампании.
          </div>

          {/* erid placement option */}
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] font-display font-semibold text-ink-60">Маркировка (erid):</span>
            <div className="flex items-center gap-1 p-0.5 rounded-pill bg-ink-6">
              <button
                type="button"
                onClick={() => patchVariant(active.id, { eridOnImage: false })}
                className={cn(
                  "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
                  !active.eridOnImage ? "bg-white shadow-sm text-ink" : "text-ink-40 hover:text-ink",
                )}
              >
                В тексте
              </button>
              <button
                type="button"
                disabled={!hasMedia}
                onClick={() => patchVariant(active.id, { eridOnImage: true })}
                title={hasMedia ? undefined : "Сначала добавьте изображение или видео"}
                className={cn(
                  "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
                  active.eridOnImage ? "bg-white shadow-sm text-ink" : "text-ink-40 hover:text-ink",
                  !hasMedia && "opacity-40 cursor-not-allowed",
                )}
              >
                На изображении
              </button>
            </div>
          </div>
        </Card>

        {/* Preview panel */}
        <div className="grid gap-2.5 sticky top-4">
          <div className="flex items-center gap-2">
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 flex-1">
              Превью · {PREVIEW_PLATFORMS.find(p => p.key === previewPlatform)?.label ?? "Telegram"}
            </div>
            {/* Platform switcher — only when >1 platform in campaign */}
            {campaignPlatforms.length > 1 && (
              <div className="flex items-center gap-1 p-0.5 rounded-pill bg-ink-6">
                {PREVIEW_PLATFORMS.filter(pp => campaignPlatforms.includes(pp.key as never)).map(pp => (
                  <button
                    key={pp.key}
                    onClick={() => setPreviewPlatform(pp.key)}
                    className={cn(
                      "inline-flex items-center gap-1 h-6 px-2 rounded-pill text-[11px] font-display font-semibold transition-colors",
                      previewPlatform === pp.key ? "bg-white shadow-sm text-ink" : "text-ink-40 hover:text-ink",
                    )}
                  >
                    <PlatformIcon platform={pp.key as Platform} size={11} />
                    {pp.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <TelegramPost
            p={{ name: active.name, text: active.text, media: active.media ?? [], link: active.link, erid: active.erid, eridOnImage: active.eridOnImage }}
            platform={previewPlatform}
          />
        </div>
      </div>

      {allCreativesOpen && (
        <AllCreativesModal
          variants={variants}
          onClose={() => setAllCreativesOpen(false)}
          onRename={renameVariant}
        />
      )}

      {helpOpen && <FormattingHelpModal onClose={() => setHelpOpen(false)} />}
    </>
  );
}

const HINTS = [
  { head: "Структура",  body: "Крючок → суть (выгода) → CTA. Три абзаца — каждый об одном." },
  { head: "Длина",      body: "Посев: 300–600 символов. Натив: до 1200. Длиннее — читают хуже." },
  { head: "Ссылка",     body: "Ставьте в первый или последний абзац — не закапывайте в середину." },
  { head: "CTA",        body: "Одно действие: «Перейти», «Забрать скидку», «Узнать цену»." },
  { head: "Эмодзи",    body: "До 3 штук на пост. Акцент, а не украшение." },
];

function HintsPopover({ onClose, anchorRef }: { onClose: () => void; anchorRef: React.RefObject<HTMLDivElement | null> }) {
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [onClose, anchorRef]);

  return (
    <div className="absolute right-0 top-full mt-1.5 z-50 w-64 bg-white border border-ink-12 rounded-xl shadow-lg p-3 grid gap-2 fade-up">
      {HINTS.map((h) => (
        <div key={h.head} className="grid gap-0.5">
          <div className="text-[11px] font-display font-bold text-ink">{h.head}</div>
          <div className="text-[12px] text-ink-60 leading-snug">{h.body}</div>
        </div>
      ))}
    </div>
  );
}

function VariantTabs({ variants, activeId, setActiveId, addVariant, removeVariant, openAllCreatives }: {
  variants: CreativeVariant[]; activeId: string; setActiveId: (id: string) => void;
  addVariant: () => void; removeVariant: (id: string) => void; openAllCreatives: () => void;
}) {
  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <span className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mr-1">A/B варианты</span>
      {variants.map((v) => (
        <button key={v.id} onClick={() => setActiveId(v.id)}
          className={cn("inline-flex items-center gap-1.5 h-8 px-3 rounded-pill text-[12.5px] font-display font-semibold transition-colors",
            activeId === v.id ? "bg-ink text-white" : "bg-ink-6 text-ink-60 hover:text-ink")}>
          {v.name}
          {variants.length > 1 && (
            <span onClick={(e) => { e.stopPropagation(); removeVariant(v.id); }}
              className="ml-1 opacity-70 hover:opacity-100"><X size={11} /></span>
          )}
        </button>
      ))}
      {variants.length < MAX_VARIANTS && (
        <button onClick={addVariant} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink">
          <Plus size={13} strokeWidth={3} /> Вариант
        </button>
      )}
      {variants.length > 1 && (
        <button onClick={openAllCreatives}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink">
          <LayoutGrid size={13} /> Показать все ({variants.length})
        </button>
      )}
      <span className="ml-auto text-[11.5px] text-ink-40 font-mono">{variants.length} / {MAX_VARIANTS}</span>
    </div>
  );
}


const FORMAT_ROWS: { syntax: string; result: string; desc: string }[] = [
  { syntax: "**текст**",           result: "жирный",       desc: "Акцент на ключевом слове" },
  { syntax: "__текст__",           result: "курсив",        desc: "Название, цитата, термин" },
  { syntax: "~текст~",             result: "подчёркнутый",  desc: "Ссылка без гиперссылки" },
  { syntax: "[текст](https://…)",  result: "ссылка",        desc: "Кликабельный текст" },
  { syntax: ":name:",              result: "кастомный эмодзи", desc: "Загрузите свой в палитре" },
];

function FormattingHelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.45)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-[500px] shadow-lg fade-up grid gap-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-[16px]">Разметка текста</h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center rounded-sm hover:bg-ink-6"><X size={14} /></button>
        </div>

        <table className="w-full text-[12.5px] border-collapse">
          <thead>
            <tr className="text-left text-[10.5px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
              <th className="pb-2 pr-4">Синтаксис</th>
              <th className="pb-2 pr-4">Результат</th>
              <th className="pb-2">Когда использовать</th>
            </tr>
          </thead>
          <tbody>
            {FORMAT_ROWS.map((r) => (
              <tr key={r.syntax} className="border-t border-ink-12/50">
                <td className="py-2 pr-4">
                  <code className="bg-ink-6 px-1.5 py-0.5 rounded-sm font-mono text-[11.5px] whitespace-nowrap">{r.syntax}</code>
                </td>
                <td className="py-2 pr-4 text-ink-60 whitespace-nowrap">{r.result}</td>
                <td className="py-2 text-ink-40">{r.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="rounded-md bg-cream p-3 grid gap-1.5">
          <div className="text-[10.5px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">Пример</div>
          <code className="text-[12px] font-mono text-ink-60 leading-relaxed whitespace-pre-wrap">{`**Нашли курс, который реально работает 🔥**\n\nУже 3 000 студентов прошли — средний рост дохода +40%.\n\n[Смотреть программу](https://example.com) — первые 2 урока бесплатно.`}</code>
        </div>

        <div className="text-[11.5px] text-ink-40 leading-relaxed">
          Превью справа рендерит разметку в реальном времени — жирный, курсив, ссылки и кастомные эмодзи отображаются так, как увидит подписчик.
        </div>
      </div>
    </div>
  );
}

function AllCreativesModal({ variants, onClose, onRename }: {
  variants: CreativeVariant[]; onClose: () => void; onRename: (id: string, name: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--bg)] rounded-lg p-6 w-[min(1200px,94vw)] max-h-[88vh] overflow-y-auto shadow-lg fade-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-[18px]">Все креативы ({variants.length})</h3>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-x-5 gap-y-7">
          {variants.map((v) => (
            <div key={v.id} className="flex flex-col gap-2.5">
              <div className="relative group flex items-center gap-1.5 self-start">
                <input
                  value={v.name}
                  onChange={(e) => onRename(v.id, e.target.value)}
                  className="font-display font-bold text-[15px] bg-transparent border-0 outline-none focus:bg-ink-6 px-1 rounded-sm max-w-[280px]"
                />
                <Pencil size={11} className="text-ink-30 group-hover:text-ink-60 transition-colors pointer-events-none flex-shrink-0" />
              </div>
              <TelegramPost p={{ name: v.name, text: v.text, media: v.media ?? [], link: v.link, erid: v.erid }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
